/**
 * Utility functions for applying text styles and gradients from API response
 */

export interface TextStyle {
  textStyle?: string;
  textColor?: string;
  textGradientColors?: string;
  gradientAngle?: number;
  isStrikeThrough?: boolean;
}

/**
 * Maps text style names to CSS classes
 */
const getTextStyleClass = (textStyle?: string): { fontSize: string; fontWeight: string; lineHeight: string } => {
  const styleMap: Record<string, { fontSize: string; fontWeight: string; lineHeight: string }> = {
    h1: { fontSize: '32px', fontWeight: '700', lineHeight: '38px' },
    h2: { fontSize: '24px', fontWeight: '600', lineHeight: '29px' },
    h3: { fontSize: '20px', fontWeight: '600', lineHeight: '24px' },
    h4: { fontSize: '18px', fontWeight: '600', lineHeight: '22px' },
    h5: { fontSize: '14px', fontWeight: '600', lineHeight: '14px' },
    h6: { fontSize: '12px', fontWeight: '600', lineHeight: '16px' },
    b1_med: { fontSize: '16px', fontWeight: '500', lineHeight: '20px' },
    b2_reg: { fontSize: '14px', fontWeight: '400', lineHeight: '20px' },
    b2_med: { fontSize: '14px', fontWeight: '500', lineHeight: '20px' },
    b3_reg: { fontSize: '12px', fontWeight: '400', lineHeight: '16px' },
    b3_med: { fontSize: '12px', fontWeight: '500', lineHeight: '16px' },
  };

  return styleMap[textStyle || ''] || { fontSize: '14px', fontWeight: '400', lineHeight: '20px' };
};

/**
 * Creates gradient CSS string from colors and angle
 */
const createGradient = (colors: string, angle: number = 90): string => {
  const colorArray = colors.split(',').map(c => c.trim());
  return `linear-gradient(${angle}deg, ${colorArray.join(', ')})`;
};

/**
 * Gets inline styles for text based on API response style object
 */
export const getTextStyles = (style?: TextStyle): React.CSSProperties => {
  if (!style) {
    return {};
  }

  const baseStyle = getTextStyleClass(style.textStyle);
  const styles: React.CSSProperties = {
    fontFamily: "'Noto Sans', sans-serif",
    fontSize: baseStyle.fontSize,
    fontWeight: baseStyle.fontWeight,
    lineHeight: baseStyle.lineHeight,
  };

  // Apply text color or gradient
  if (style.textGradientColors) {
    const gradient = createGradient(style.textGradientColors, style.gradientAngle || 90);
    styles.background = gradient;
    styles.backgroundClip = 'text';
    styles.WebkitBackgroundClip = 'text';
    styles.WebkitTextFillColor = 'transparent';
    styles.color = 'transparent';
  } else if (style.textColor) {
    styles.color = style.textColor;
  }

  // Apply strike through
  if (style.isStrikeThrough) {
    styles.textDecoration = 'line-through';
    styles.textDecorationSkipInk = 'none';
  }

  return styles;
};

/**
 * Gets CSS class name for gradient text (for use with CSS modules)
 */
export const getGradientTextClass = (colors: string, angle: number = 90): string => {
  // This can be used to generate dynamic gradient classes if needed
  return '';
};

